<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { useAppConfig } from '@/composables/useAppConfig'
import Toast from 'primevue/toast'
import type { LayoutType } from '@/router'

// Lazy load layouts for better performance
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import AppLayout from '@/layouts/AppLayout.vue'

const { config, setPageTitle, resetPageTitle } = useAppConfig()
const route = useRoute()

// Map layout names to components
const layoutComponents = {
  default: DefaultLayout,
  auth: AuthLayout,
  app: AppLayout,
}

// Get the current layout component based on route meta
const currentLayout = computed(() => {
  const layoutName = (route.meta?.layout as LayoutType) || 'default'
  return layoutComponents[layoutName] || layoutComponents.default
})

// Set initial document title and meta description
onMounted(() => {
  // Set default meta description if not present
  let metaDescription = document.querySelector('meta[name="description"]')
  if (!metaDescription) {
    metaDescription = document.createElement('meta')
    metaDescription.setAttribute('name', 'description')
    document.head.appendChild(metaDescription)
  }
  metaDescription.setAttribute('content', config.seo.defaultDescription)

  // Set keywords meta tag
  let metaKeywords = document.querySelector('meta[name="keywords"]')
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta')
    metaKeywords.setAttribute('name', 'keywords')
    document.head.appendChild(metaKeywords)
  }
  metaKeywords.setAttribute('content', config.seo.keywords.join(', '))
})

// Update page title based on route meta
watch(
  () => route.meta?.title,
  (title) => {
    if (title && typeof title === 'string') {
      setPageTitle(title)
    } else {
      resetPageTitle()
    }
  },
  { immediate: true }
)
</script>

<template>
  <Toast />
  <component :is="currentLayout">
    <RouterView v-slot="{ Component }">
      <Transition
        mode="out-in"
        enter-active-class="tw-transition-opacity tw-duration-200"
        enter-from-class="tw-opacity-0"
        enter-to-class="tw-opacity-100"
        leave-active-class="tw-transition-opacity tw-duration-150"
        leave-from-class="tw-opacity-100"
        leave-to-class="tw-opacity-0"
      >
        <component :is="Component" />
      </Transition>
    </RouterView>
  </component>
</template>

<style scoped>
/* App-level styles */
</style>
